import { api } from "@/lib/api";
import { PaymentOutput } from "@/types/payment.types";

export interface PaymentResponse {
  status: string;
  message: string;
  data: PaymentOutput[];
}

export interface PaymentOverview {
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
}

export interface PaymentOverviewResponse {
  status: string;
  message: string;
  data: PaymentOverview;
}

export interface PaymentFilterParams {
  startDate?: string;
  endDate?: string;
}

export const paymentService = {
  async getAllPayments(params?: PaymentFilterParams): Promise<PaymentOutput[]> {
    try {
      const response = await api.get<PaymentResponse>("/payments", { params });
      return Array.isArray(response.data.data)
        ? response.data.data
        : [response.data.data];
    } catch (error) {
      console.error("Error fetching payments:", error);
      throw error;
    }
  },

  async getPaymentOverview(params?: PaymentFilterParams): Promise<PaymentOverview> {
    try {
      const response = await api.get<PaymentOverviewResponse>("/payments/overview", { params });

      // Handle different response structures
      const overviewData = response.data.data || response.data;
      console.log("Extracted overview data:", overviewData);

      // Ensure we have the correct structure
      if (overviewData && typeof overviewData === 'object') {
        return {
          totalAmount: overviewData.totalAmount || 0,
          paidAmount: overviewData.paidAmount || 0,
          pendingAmount: overviewData.pendingAmount || 0,
        };
      }

      throw new Error("Invalid payment overview response structure");
    } catch (error) {
      console.error("Error fetching payment overview:", error);
      throw error;
    }
  },

  async updatePaymentStatus(id: string, status: string): Promise<PaymentOutput> {
    try {
      const response = await api.patch<{ status: string; message: string; data: PaymentOutput }>(
        `/payments/${id}/status`,
        { status }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating payment status:", error);
      throw error;
    }
  },

  async updatePaymentAmount(id: string, amount: number): Promise<PaymentOutput> {
    try {
      const response = await api.patch<{ status: string; message: string; data: PaymentOutput }>(
        `/payments/${id}/amount`,
        { totalAmount: amount }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating payment amount:", error);
      throw error;
    }
  },

  async getPaymentById(id: string): Promise<PaymentOutput> {
    try {
      const response = await api.get<{ status: string; message: string; data: PaymentOutput }>(
        `/payments/${id}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching payment by ID:", error);
      throw error;
    }
  },

  async addPartialPayment(
    id: string,
    amount: number,
    mode?: string,
    notes?: string
  ): Promise<PaymentOutput> {
    try {
      const response = await api.post<{ status: string; message: string; data: PaymentOutput }>(
        `/payments/${id}/partial-payment`,
        { amount, mode, notes }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error adding partial payment:", error);
      throw error;
    }
  },

  async getPaymentByRefId(refId: string): Promise<PaymentOutput | null> {
    try {
      const response = await api.get<{ status: string; message: string; data: PaymentOutput }>(
        `/payments/by-ref/${refId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching payment by refId:", error);
      return null;
    }
  },

  async updateDiscount(id: string, discount: number): Promise<PaymentOutput> {
    try {
      const response = await api.patch<{ status: string; message: string; data: PaymentOutput }>(
        `/payments/${id}/discount`,
        { discount }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating discount:", error);
      throw error;
    }
  },
};
